import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";

// ─── Warp Speed Canvas ────────────────────────────────────────────────────────
function WarpSpeed({ speed = 1 }) {
  const canvasRef = useRef(null);
  const speedRef  = useRef(speed);

  useEffect(() => { speedRef.current = speed; }, [speed]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const W = () => canvas.width;
    const H = () => canvas.height;

    // Stars emanate from center
    const STARS = 320;
    const stars = Array.from({ length: STARS }, () => ({
      angle: Math.random() * Math.PI * 2,
      dist:  Math.random() * Math.max(W(), H()) * 0.5,
      size:  Math.random() * 1.8 + 0.3,
      alpha: Math.random() * 0.6 + 0.4,
      speed: Math.random() * 2.5 + 0.8,
    }));

    const draw = () => {
      const w = W(), h = H();
      const cx = w / 2, cy = h / 2;
      const s  = speedRef.current;

      ctx.fillStyle = `rgba(2,4,15,${s > 5 ? 0.6 : 0.85})`;
      ctx.fillRect(0, 0, w, h);

      // Nebula glow at center
      const ng = ctx.createRadialGradient(cx, cy, 0, cx, cy, 300);
      ng.addColorStop(0,   `rgba(0,80,200,${0.04 * s})`);
      ng.addColorStop(0.5, `rgba(100,0,200,${0.02 * s})`);
      ng.addColorStop(1,   "rgba(0,0,0,0)");
      ctx.fillStyle = ng;
      ctx.fillRect(0, 0, w, h);

      stars.forEach(star => {
        star.dist += star.speed * s * 1.8;
        if (star.dist > Math.max(w, h) * 0.75) {
          star.dist  = Math.random() * 20;
          star.angle = Math.random() * Math.PI * 2;
        }

        const x   = cx + Math.cos(star.angle) * star.dist;
        const y   = cy + Math.sin(star.angle) * star.dist;
        const len = Math.min(star.speed * s * 3, 30);
        const x0  = cx + Math.cos(star.angle) * (star.dist - len);
        const y0  = cy + Math.sin(star.angle) * (star.dist - len);

        const alpha = Math.min(star.alpha * (star.dist / 200), 1);
        const grad  = ctx.createLinearGradient(x0, y0, x, y);
        grad.addColorStop(0, "rgba(255,255,255,0)");
        grad.addColorStop(1, `rgba(180,210,255,${alpha})`);

        ctx.save();
        ctx.strokeStyle = grad;
        ctx.lineWidth   = star.size * Math.min(s * 0.5, 2.5);
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x, y);
        ctx.stroke();
        ctx.restore();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full" style={{ zIndex: 0 }} />;
}

// ─── Launch Page ──────────────────────────────────────────────────────────────
export default function LaunchPage() {
  const { user }        = useAuth();
  const navigate        = useNavigate();
  const [phase, setPhase] = useState("enter");   // enter → warp → flash → done
  const [warpSpeed, setWarpSpeed] = useState(1);
  const [flash, setFlash]         = useState(false);

  const name = user?.full_name?.split(" ")[0] || "Astronauta";

  useEffect(() => {
    // Limpiar cualquier estilo residual del body que pueda haber dejado la página anterior
    document.body.style.overflow    = "";
    document.body.style.pointerEvents = "";
    document.documentElement.style.overflow = "";

    // If no token, skip to dashboard (direct access without auth)
    if (!localStorage.getItem("token")) { navigate("/", { replace: true }); return; }

    // Sequence
    const t1 = setTimeout(() => { setPhase("warp");  setWarpSpeed(3);  }, 1200);
    const t2 = setTimeout(() => { setWarpSpeed(9);  }, 2200);
    const t3 = setTimeout(() => { setWarpSpeed(18); }, 2800);
    const t4 = setTimeout(() => { setFlash(true); setPhase("flash"); }, 3300);
    // Navegar ANTES de que el flash termine para que el overlay no quede visible
    const t5 = setTimeout(() => { navigate("/", { replace: true }); }, 3700);

    return () => {
      [t1, t2, t3, t4, t5].forEach(clearTimeout);
      // Restaurar body al desmontar para garantizar que el dashboard no queda bloqueado
      document.body.style.overflow    = "";
      document.body.style.pointerEvents = "";
      document.documentElement.style.overflow = "";
    };
  }, [navigate]);

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center"
      style={{ background: "#020409" }}>

      <WarpSpeed speed={warpSpeed} />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-8 text-center px-6 select-none">

        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <img src="/valoriza-logo.svg" alt="Valoriza" className="h-8 w-auto object-contain opacity-80" />
        </motion.div>

        {/* Rocket */}
        <motion.div
          initial={{ scale: 0, rotate: -20 }}
          animate={
            phase === "warp"
              ? { scale: [1, 1.3, 0.8], y: [0, -10, -400], rotate: [0, 5, 0], opacity: [1, 1, 0] }
              : { scale: 1, rotate: 0 }
          }
          transition={
            phase === "warp"
              ? { duration: 2.2, ease: [0.4, 0, 0.2, 1] }
              : { duration: 0.6, delay: 0.2, type: "spring", stiffness: 180 }
          }
          className="relative"
        >
          {/* Rocket SVG */}
          <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Body */}
            <path d="M40 8 C40 8 26 22 26 44 L40 56 L54 44 C54 22 40 8 40 8Z"
              fill="url(#rocketBody)" />
            {/* Window */}
            <circle cx="40" cy="34" r="7" fill="url(#window)" />
            <circle cx="40" cy="34" r="4" fill="rgba(180,220,255,0.6)" />
            {/* Wings */}
            <path d="M26 44 L16 56 L28 52Z" fill="url(#wing)" />
            <path d="M54 44 L64 56 L52 52Z" fill="url(#wing)" />
            {/* Flame */}
            <motion.path
              d="M34 56 C34 56 32 64 40 70 C48 64 46 56 46 56Z"
              fill="url(#flame)"
              animate={{ scaleY: [1, 1.4, 0.9, 1.2], opacity: [0.9, 1, 0.7, 1] }}
              transition={{ repeat: Infinity, duration: 0.3, ease: "easeInOut" }}
            />
            <motion.path
              d="M36 56 C36 56 34 62 40 66 C46 62 44 56 44 56Z"
              fill="rgba(255,220,80,0.9)"
              animate={{ scaleY: [1, 1.3, 0.8], opacity: [1, 0.8, 1] }}
              transition={{ repeat: Infinity, duration: 0.25 }}
            />
            <defs>
              <linearGradient id="rocketBody" x1="40" y1="8" x2="40" y2="56" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#e2e8f0" />
                <stop offset="100%" stopColor="#94a3b8" />
              </linearGradient>
              <radialGradient id="window" cx="50%" cy="40%" r="50%">
                <stop offset="0%" stopColor="#60a5fa" />
                <stop offset="100%" stopColor="#1d4ed8" />
              </radialGradient>
              <linearGradient id="wing" x1="0" y1="0" x2="1" y2="1" gradientUnits="objectBoundingBox">
                <stop offset="0%" stopColor="#1d4ed8" />
                <stop offset="100%" stopColor="#7c3aed" />
              </linearGradient>
              <linearGradient id="flame" x1="40" y1="56" x2="40" y2="70" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#f97316" />
                <stop offset="60%" stopColor="#ef4444" />
                <stop offset="100%" stopColor="rgba(239,68,68,0)" />
              </linearGradient>
            </defs>
          </svg>

          {/* Smoke particles */}
          {phase !== "enter" && [0,1,2,3,4].map(i => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: 6 + i * 3, height: 6 + i * 3,
                bottom: -10 - i * 8,
                left: `calc(50% - ${(6 + i * 3) / 2}px)`,
                background: `rgba(${150 + i*20}, ${170 + i*15}, 255, 0.3)`,
              }}
              animate={{ scale: [1, 2.5], opacity: [0.5, 0], y: [0, 20 + i * 8] }}
              transition={{ repeat: Infinity, duration: 0.5 + i * 0.1, delay: i * 0.08 }}
            />
          ))}
        </motion.div>

        {/* Welcome text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="space-y-2"
        >
          <motion.p
            className="text-white/40 text-sm font-medium tracking-widest uppercase"
            animate={phase === "warp" ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            Bienvenido a
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-black text-white tracking-tighter"
            style={{ textShadow: "0 0 40px rgba(100,150,255,0.4)" }}
            animate={phase === "warp" ? { scale: 1.08, opacity: 0 } : { scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            {name}
          </motion.h1>
          <motion.p
            className="text-sm font-medium"
            style={{
              background: "linear-gradient(135deg, #60a5fa, #a78bfa, #34d399)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
            }}
            animate={phase === "warp" ? { opacity: 0 } : { opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            Innovation OS · Listo para despegar
          </motion.p>
        </motion.div>

        {/* Progress bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: phase === "warp" ? 0 : 1 }}
          transition={{ delay: 0.8 }}
          className="w-48 h-0.5 rounded-full overflow-hidden"
          style={{ background: "rgba(255,255,255,0.08)" }}
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, #3b82f6, #8b5cf6)" }}
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 1.1, delay: 0.6, ease: "easeInOut" }}
          />
        </motion.div>
      </div>

      {/* Flash overlay */}
      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, times: [0, 0.2, 0.6, 1] }}
            className="fixed inset-0"
            style={{
              zIndex: 50,
              background: "radial-gradient(circle at 50% 50%, rgba(255,255,255,1) 0%, rgba(29,78,216,0.8) 50%, rgba(2,4,15,0.9) 100%)",
              pointerEvents: "none",
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
